import { Component, ElementRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { TextFieldModule } from '@angular/cdk/text-field';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { NgIf, NgClass, NgFor } from '@angular/common';

import { dd2dms, dms2dd } from '../../../../shared/utils/geographic-coordinate-converter';
import { pageTransitions } from '../../../../shared/animations/page-transitions';
import {
	StepClickedEvent,
	StepStatus,
	StepperComponent
} from '../../../../shared/components/stepper/stepper.component';
import { User } from '../../../../shared/models/user';
import { LocationService } from '../../../../shared/services/location.service';
import { TranslationService } from '../../../../shared/services/translation.service';
import { UserService } from '../../../../shared/services/user.service';
import { Guarantee } from '../../../models/guarantee';
import { GuaranteeRequestService } from '../../../services/guarantee-request.service';
import { GuaranteeStatus } from '../../../models/guarantee-constants';
import { SnackbarSaveDraftGuaranteeComponent } from '../../utils/snackbar-save-draft-guarantee.component';
import { LATITUDE_OPTIONS, LATITUDE_OPTIONS_KEYS,
	LONGITUDE_OPTIONS, LONGITUDE_OPTIONS_KEYS } from '../../../../shared/models/list';
import { LoadingSpinnerComponent } from '../../../../shared/components/utils/loading-spinner.component';
import { InputNumberDirective } from '../../../../shared/directives/input-number.directive';
import { PageHeaderComponent } from '../../../../shared/components/page-header/page-header.component';

const latitudeTypeValues = LATITUDE_OPTIONS_KEYS.map(key => ({ key, value: LATITUDE_OPTIONS[key] }));
const longitudeTypeValues = LONGITUDE_OPTIONS_KEYS.map(key => ({ key, value: LONGITUDE_OPTIONS[key] }));
interface ErrorObject {
	latitude: boolean;
	longitude: boolean;
	latdegree: boolean;
	latminutes: boolean;
	latseconds: boolean;
	longdegree: boolean;
	longminutes: boolean;
	longseconds: boolean;
}
@Component({
	selector: 'copo-project-environment',
	templateUrl: './project-environment.component.html',
	styleUrl: './project-environment.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		CdkScrollable,
		PageHeaderComponent,

		MatButtonModule,
		TranslateModule,
		MatIconModule,
		FormsModule,
		StepperComponent,

		MatFormFieldModule,
		TextFieldModule,

		MatInputModule,
		ReactiveFormsModule,

		MatRadioModule,
		NgClass,

		MatSelectModule,
		NgFor,

		MatOptionModule,

		MatCheckboxModule,
		InputNumberDirective,
		LoadingSpinnerComponent
	]
})
export class ProjectEnvironmentComponent {
	@ViewChild('latitudeDiv', { read: ElementRef }) latitudeDiv!: ElementRef<HTMLElement>;
	@ViewChild('longitudeDiv', { read: ElementRef }) longitudeDiv!: ElementRef<HTMLElement>;
	@ViewChild('latDegreeDiv', { read: ElementRef }) latDegreeDiv!: ElementRef<HTMLElement>;
	@ViewChild('latMinutesDiv', { read: ElementRef }) latMinutesDiv!: ElementRef<HTMLElement>;
	@ViewChild('latSecondsDiv', { read: ElementRef }) latSecondsDiv!: ElementRef<HTMLElement>;
	@ViewChild('longDegreeDiv', { read: ElementRef }) longDegreeDiv!: ElementRef<HTMLElement>;
	@ViewChild('longMinutesDiv', { read: ElementRef }) longMinutesDiv!: ElementRef<HTMLElement>;
	@ViewChild('longSecondsDiv', { read: ElementRef }) longSecondsDiv!: ElementRef<HTMLElement>;
	@ViewChild('constructionSiteTitle', { read: ElementRef }) constructionSiteRef!: ElementRef<HTMLElement>;
	submitting = false;
	guarantee: Guarantee;
	coordinatedms = false;
	coordinatedd = true;
	onErrorMandatory: ErrorObject = {
		latitude: false,
		longitude: false,
		latdegree: false,
		latminutes: false,
		latseconds: false,
		longdegree: false,
		longminutes: false,
		longseconds: false

	};

	onErrorConstructionSite = false;
	onErrorCoordinates = false;

	user: User;
	readonly steps;

	latitudeFormControl: FormControl<number | null>;
	latitudeDegreeFormControl: FormControl<number | null>;
	latitudeMinutesFormControl: FormControl<number | null>;
	latitudeSecondsFormControl: FormControl<number | null>;
	latitudeDirectionFormControl: FormControl<string>;
	longitudeFormControl: FormControl<number | null>;
	longitudeDegreeFormControl: FormControl<number | null>;
	longitudeMinutesFormControl: FormControl<number | null>;
	longitudeSecondsFormControl: FormControl<number | null>;
	longitudeDirectionFormControl: FormControl<string>;
	buildingEnvAltitudeFormControl: FormControl<boolean>;
	ruralArea20KMFormControl: FormControl<boolean>;
	distanceToSeaCheckFormControl: FormControl<boolean>;
	specifyDistanceToSeaFormControl: FormControl<string>;
	addressFormControl: FormControl<string>;
	ruralAreaFormControl: FormControl<boolean>;
	proximityFormControl: FormControl<boolean>;
	distanceFromSiteFormControl: FormControl<string>;
	hazardousIndustryDistanceFormControl: FormControl<string>;
	urbanAgglomerationFormControl: FormControl<boolean>;
	mainlyRefinerieFormControl: FormControl<boolean>;
	industryTypeFormControl: FormControl<string>;
	emissionsTypeFormControl: FormControl<string>;
	gasCarriedWindsFormControl: FormControl<string>;
	desertFormControl: FormControl<boolean>;
	areaNameFormControl: FormControl<string>;
	coastalOrMarineFormControl: FormControl<boolean>;
	coastFormControl: FormControl<boolean>;
	directWettingFormControl: FormControl<boolean>;
	partialOrMomentaryFormControl: FormControl<boolean>;
	betweenTwoFormControl: FormControl<boolean>;
	betweenTenFormControl: FormControl<boolean>;
	specificEnvironmentFormControl: FormControl<boolean>;
	distanceToLakeFormControl: FormControl<string>;
	distanceToSeaFormControl: FormControl<string>;

	readonly latitudeTypeValues = latitudeTypeValues;
	readonly longitudeTypeValues = longitudeTypeValues;

	constructor(
		private readonly locationService: LocationService,
		private readonly translationService: TranslationService,
		private readonly userService: UserService,

		private readonly snackBar: MatSnackBar,
		private readonly router: Router,
		private readonly route: ActivatedRoute,
		private readonly guaranteeService: GuaranteeRequestService,

	) {
		this.steps = [
			{
				label: this.translationService.getTR('guarantee.project.characteristics'),
				status: StepStatus.ENABLED,
			},
			{
				label: this.translationService.getTR('guarantee.project.environment'),
				status: StepStatus.CURRENT,
			},
			{
				label: this.translationService.getTR('guarantee.type'),
				status: StepStatus.DISABLED,
			},
			{
				label: this.translationService.getTR('guarantee.summary'),
				status: StepStatus.DISABLED,
			}
		];
		const res = <{ guarantee: Guarantee }> this.route.snapshot.data;
		this.guarantee = res['guarantee'];
		this.user = this.userService.user;
		this.latitudeFormControl = new FormControl(
			this.guarantee.latitude !== '' ? Number(this.guarantee.latitude) : null,
			Validators.pattern(/^-?\d*[,.]?\d{0,6}$/)
		);
		this.latitudeDegreeFormControl = new FormControl(null);
		this.latitudeMinutesFormControl = new FormControl(null);
		this.latitudeSecondsFormControl = new FormControl(null);
		this.latitudeDirectionFormControl = new FormControl('N', { nonNullable: true });
		this.longitudeFormControl = new FormControl(
			this.guarantee.longitude !== '' ? Number(this.guarantee.longitude) : null,
			Validators.pattern(/^-?\d*[,.]?\d{0,6}$/)
		);
		this.longitudeDegreeFormControl = new FormControl(null);
		this.longitudeMinutesFormControl = new FormControl(null);
		this.longitudeSecondsFormControl = new FormControl(null);
		this.longitudeDirectionFormControl = new FormControl('E', { nonNullable: true });
		this.buildingEnvAltitudeFormControl = new FormControl(
			this.guarantee.buildingEnvAltitude,
			{ nonNullable: true }
		);
		this.ruralArea20KMFormControl = new FormControl(
			this.guarantee.buildingEnvBLMoreThan20KMFromCoast,
			{ nonNullable: true }
		);
		this.distanceToSeaCheckFormControl = new FormControl(
			this.guarantee.buildingEnvBLSpecifyDistance,
			{ nonNullable: true }
		);
		this.specifyDistanceToSeaFormControl = new FormControl(
			this.guarantee.buildingEnvBLPleaseSpecify,
			{ nonNullable: true }
		);
		this.addressFormControl = new FormControl(this.guarantee.address, { nonNullable: true });
		this.ruralAreaFormControl = new FormControl(this.guarantee.ruralAreaDistantFromCoast, { nonNullable: true });
		this.proximityFormControl = new FormControl(this.guarantee.proximityBuildingAnimals, { nonNullable: true });
		this.distanceFromSiteFormControl = new FormControl(this.guarantee.animalDistance, { nonNullable: true });
		this.hazardousIndustryDistanceFormControl = new FormControl(
			this.guarantee.hazardousIndustryDistance,
			{ nonNullable: true }
		);
		this.urbanAgglomerationFormControl = new FormControl(
			this.guarantee.urbanOrIndustrialArea,
			{ nonNullable: true }
		);
		this.mainlyRefinerieFormControl = new FormControl(
			this.guarantee.mainlyComprisingRefinerie,
			{ nonNullable: true }
		);
		this.industryTypeFormControl = new FormControl(this.guarantee.typeOfIndustry, { nonNullable: true });
		this.emissionsTypeFormControl = new FormControl(this.guarantee.typeOfEmissions, { nonNullable: true });
		if (this.guarantee.applicationType === 'Magnelis-Solar'
			|| this.guarantee.applicationType === 'Magnelis-Other application'
			|| this.guarantee.applicationType === 'Magnelis-Building') {
			this.guarantee.gasCarriedWinds = 'false';
			this.gasCarriedWindsFormControl = new FormControl(this.guarantee.gasCarriedWinds, { nonNullable: true });
		} else {
			this.guarantee.gasCarriedWinds = '';
			this.gasCarriedWindsFormControl = new FormControl(this.guarantee.gasCarriedWinds, { nonNullable: true });
		}
		this.desertFormControl = new FormControl(this.guarantee.inDesert, { nonNullable: true });
		this.areaNameFormControl = new FormControl(this.guarantee.desertComment, { nonNullable: true });
		this.coastalOrMarineFormControl = new FormControl(
			this.guarantee.coastalMarineEnvironment,
			{ nonNullable: true }
		);
		this.coastFormControl = new FormControl(this.guarantee.lessThan300mfromCoast, { nonNullable: true });
		this.directWettingFormControl = new FormControl(this.guarantee.withoutWettingSeaWater, { nonNullable: true });
		this.partialOrMomentaryFormControl = new FormControl(
			this.guarantee.partialWettingSeaWater,
			{ nonNullable: true }
		);
		this.betweenTwoFormControl = new FormControl(this.guarantee.between300m2kmfromCoast, { nonNullable: true });
		this.betweenTenFormControl = new FormControl(this.guarantee.between2km10kmfromCoast, { nonNullable: true });
		this.specificEnvironmentFormControl = new FormControl(
			this.guarantee.specificEnvironment,
			{ nonNullable: true }
		);
		this.distanceToLakeFormControl = new FormControl(this.guarantee.distancetoLakeRiver, { nonNullable: true });
		this.distanceToSeaFormControl = new FormControl(this.guarantee.distanceToSea, { nonNullable: true });
	}

	goBack() {
		this.locationService.go(-1, this.guarantee.id !== undefined
			? `../guarantee-requests/edit/${ this.guarantee.id }/project-characteristics`
			: '../guarantee-requests/new/project-characteristics');
	}

	resetOnError() {
		this.onErrorMandatory.latitude = false;
		this.onErrorMandatory.longitude = false;
		this.onErrorMandatory.latdegree = false;
		this.onErrorMandatory.latminutes = false;
		this.onErrorMandatory.latseconds = false;
		this.onErrorMandatory.longdegree = false;
		this.onErrorMandatory.longminutes = false;
		this.onErrorMandatory.longseconds = false;
	}

	onErrorForm(elem: ElementRef, onError: keyof ErrorObject, event: Event) {
		(<HTMLElement> elem.nativeElement).scrollIntoView();
		this.onErrorMandatory[onError] = true;
		event.preventDefault();
	}

	isLatitudeValid(event: Event) {
		if (this.latitudeFormControl.hasError('pattern') || this.latitudeFormControl.hasError('max')
			|| this.latitudeFormControl.hasError('min')) {
			this.onErrorForm(this.latitudeDiv, 'latitude', event);
			this.latitudeFormControl.markAsTouched();
			this.latitudeFormControl.updateValueAndValidity();
			return false;
		}
		return true;
	}

	isLatDegreeValid(event: Event) {
		if (this.latitudeDegreeFormControl.hasError('max')
			|| this.latitudeDegreeFormControl.hasError('min')) {
			this.onErrorForm(this.latDegreeDiv, 'latdegree', event);
			this.latitudeDegreeFormControl.markAsTouched();
			this.latitudeDegreeFormControl.updateValueAndValidity();
			return false;
		}
		return true;
	}

	isLatMinutesValid(event: Event) {
		if (this.latitudeMinutesFormControl.hasError('max')
			|| this.latitudeMinutesFormControl.hasError('min')) {
			this.onErrorForm(this.latMinutesDiv, 'latminutes', event);
			this.latitudeMinutesFormControl.markAsTouched();
			this.latitudeMinutesFormControl.updateValueAndValidity();
			return false;
		}
		return true;
	}

	isLatSecondsValid(event: Event) {
		if (this.latitudeSecondsFormControl.hasError('pattern') || this.latitudeSecondsFormControl.hasError('max')
			|| this.latitudeSecondsFormControl.hasError('min')) {
			this.onErrorForm(this.latSecondsDiv, 'latseconds', event);
			this.latitudeSecondsFormControl.markAsTouched();
			this.latitudeSecondsFormControl.updateValueAndValidity();
			return false;
		}
		return true;
	}

	isLongitudeValid(event: Event) {
		if (this.longitudeFormControl.hasError('pattern') || this.longitudeFormControl.hasError('max')
			|| this.longitudeFormControl.hasError('min')) {
			this.onErrorForm(this.longitudeDiv, 'longitude', event);
			this.longitudeFormControl.markAsTouched();
			this.longitudeFormControl.updateValueAndValidity();
			return false;
		}
		return true;
	}

	isLongDegreeValid(event: Event) {
		if (this.longitudeDegreeFormControl.hasError('max')
			|| this.longitudeDegreeFormControl.hasError('min')) {
			this.onErrorForm(this.longDegreeDiv, 'longdegree', event);
			this.longitudeDegreeFormControl.markAsTouched();
			this.longitudeDegreeFormControl.updateValueAndValidity();
			return false;
		}
		return true;
	}

	isLongMinutesValid(event: Event) {
		if (this.longitudeMinutesFormControl.hasError('max')
			|| this.longitudeMinutesFormControl.hasError('min')) {
			this.onErrorForm(this.longMinutesDiv, 'longminutes', event);
			this.longitudeMinutesFormControl.markAsTouched();
			this.longitudeMinutesFormControl.updateValueAndValidity();
			return false;
		}
		return true;
	}

	isLongSecondsValid(event: Event) {
		if (this.longitudeSecondsFormControl.hasError('pattern') || this.longitudeSecondsFormControl.hasError('max')
			|| this.longitudeSecondsFormControl.hasError('min')) {
			this.onErrorForm(this.longSecondsDiv, 'longseconds', event);
			this.longitudeSecondsFormControl.markAsTouched();
			this.longitudeSecondsFormControl.updateValueAndValidity();
			return false;
		}
		return true;
	}

	hasCoordinateValue(control: FormControl<number | null>): boolean {
		return control.value !== null && control.value !== 0;
	}

	isConstructionSiteValid(event: Event): boolean {
		this.onErrorCoordinates = false;
		this.onErrorConstructionSite = false;
		const hasLatitude = this.hasCoordinateValue(this.latitudeFormControl);
		const hasLongitude = this.hasCoordinateValue(this.longitudeFormControl);
		const coordinatesEntered = this.latitudeFormControl.value !== null
			|| this.longitudeFormControl.value !== null;
		if (coordinatesEntered && !(hasLatitude && hasLongitude)) {
			this.onErrorCoordinates = true;
			this.constructionSiteRef.nativeElement.scrollIntoView();
			event.preventDefault();
			return false;
		}
		if (!(hasLatitude && hasLongitude)) {
			this.onErrorConstructionSite = true;
			this.constructionSiteRef.nativeElement.scrollIntoView();
			event.preventDefault();
			return false;
		}
		return true;
	}

	onConstructionSiteChanged(): void {
		const hasLatitude = this.hasCoordinateValue(this.latitudeFormControl);
		const hasLongitude = this.hasCoordinateValue(this.longitudeFormControl);
		const coordinatesEntered = this.latitudeFormControl.value !== null
			|| this.longitudeFormControl.value !== null;
		if (!coordinatesEntered || (hasLatitude && hasLongitude)) {
			this.onErrorCoordinates = false;
		}
		if (hasLatitude && hasLongitude) {
			this.onErrorConstructionSite = false;
		}
	}

	async goToNextStep(event: Event) {
		this.resetOnError();
		if (!this.isLatitudeValid(event) || !this.isLongitudeValid(event) || !this.isLatDegreeValid(event)
			|| !this.isLatDegreeValid(event) || !this.isLatMinutesValid(event) || !this.isLatSecondsValid(event)
			|| !this.isLongDegreeValid(event) || !this.isLongMinutesValid(event) || !this.isLongSecondsValid(event)) {
			return;
		}
		if (!this.isConstructionSiteValid(event)) {
			return;
		}
		await this.router.navigate([
			this.guarantee.id !== undefined
				? `/guarantee-requests/edit/${ this.guarantee.id }/type-of-guarantee`
				: '/guarantee-requests/new/type-of-guarantee'
		], { replaceUrl: true });
	}

	async saveGuarantee(event: Event, synchronize: boolean) {
		event.preventDefault();
		this.resetOnError();
		if (!this.isLatitudeValid(event) || !this.isLongitudeValid(event) || !this.isLatDegreeValid(event)
			|| !this.isLatDegreeValid(event) || !this.isLatMinutesValid(event) || !this.isLatSecondsValid(event)
			|| !this.isLongDegreeValid(event) || !this.isLongMinutesValid(event) || !this.isLongSecondsValid(event)) {
			return;
		}
		const now = moment().toISOString();
		const userName = `${ this.user.firstname } ${ this.user.lastname }`;
		const userId = this.user.id;
		if (this.guarantee.status !== GuaranteeStatus.DRAFT) {
			this.guarantee.requestDate = now;
		}
		this.guarantee.requesterName = userName;
		this.guarantee.requesterId = userId;

		this.submitting = true;
		try {
			if (!synchronize) {
				this.guaranteeService.guarantee = this.guarantee;
				return;
			}
			await this.guaranteeService.saveGuarantee(this.guarantee);
		} catch (err) {
			console.error(err);
			this.guarantee.status = GuaranteeStatus.DRAFT;
			this.snackBar.openFromComponent(
				SnackbarSaveDraftGuaranteeComponent,
				{
					duration: 5000,
					panelClass: ['copo-claim-draft-snackbar'],
					data: true
				}
			);
			return;
		} finally {
			this.submitting = false;
		}
		if (this.guarantee.status === GuaranteeStatus.DRAFT) {
			await this.router.navigate(['/guarantee-requests/drafts'], { replaceUrl: true });
			this.snackBar.openFromComponent(
				SnackbarSaveDraftGuaranteeComponent,
				{
					duration: 5000,
					panelClass: ['copo-claim-draft-snackbar']
				}
			);
		}
	}

	async onStepClicked(event: StepClickedEvent) {
		if (event.stepIndex === 0) {
			await this.router.navigate([
				this.guarantee.id !== undefined
					? `/guarantee-requests/edit/${ this.guarantee.id }/project-characteristics`
					: '/guarantee-requests/new/project-characteristics'
			], { replaceUrl: true });
		}
	}

	latitudeChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.latitude = target.value;
		this.onConstructionSiteChanged();
	}

	longitudeChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.longitude = target.value;
		this.onConstructionSiteChanged();
	}

	addressChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.address = target.value;
		this.onConstructionSiteChanged();
	}

	coordinateChange($event: MatRadioChange) {
		if ($event.value === 'dms') {
			this.coordinatedms = true;
			this.coordinatedd = false;
			const latlong = dd2dms([
				this.guarantee.latitude !== '' ? Number(this.guarantee.latitude) : 0,
				this.guarantee.longitude !== '' ? Number(this.guarantee.longitude) : 0
			]);
			this.latitudeDegreeFormControl.setValue(latlong[0][0]);
			this.latitudeMinutesFormControl.setValue(latlong[0][1]);
			this.latitudeSecondsFormControl.setValue(latlong[0][2]);
			this.latitudeDirectionFormControl.setValue(latlong[0][3] < 0 ? 'S' : 'N');
			this.longitudeDegreeFormControl.setValue(latlong[1][0]);
			this.longitudeMinutesFormControl.setValue(latlong[1][1]);
			this.longitudeSecondsFormControl.setValue(latlong[1][2]);
			this.longitudeDirectionFormControl.setValue(latlong[1][3] < 0 ? 'W' : 'E');
		} else {
			this.coordinatedms = false;
			this.coordinatedd = true;
			this.dmsValueChange();
		}
	}

	dmsValueChange() {
		const latlongDecimal = dms2dd([
			[
				this.latitudeDegreeFormControl.value || 0,
				this.latitudeMinutesFormControl.value || 0,
				this.latitudeSecondsFormControl.value || 0,
				this.latitudeDirectionFormControl.value === 'S' ? -1 : 1
			],
			[
				this.longitudeDegreeFormControl.value || 0,
				this.longitudeMinutesFormControl.value || 0,
				this.longitudeSecondsFormControl.value || 0,
				this.longitudeDirectionFormControl.value === 'W' ? -1 : 1
			]
		]);
		const [latitude, longitude] = latlongDecimal;
		this.guarantee.latitude = latitude === 0 ? '' : String(latitude);
		this.latitudeFormControl.setValue(latitude === 0 ? null : latitude);
		this.guarantee.longitude = longitude === 0 ? '' : String(longitude);
		this.longitudeFormControl.setValue(longitude === 0 ? null : longitude);
		this.onConstructionSiteChanged();
	}

	onRuralAreaCheckboxChange(checked: boolean) {
		this.guarantee.ruralAreaDistantFromCoast = checked;
		if (!checked) {
			this.onProximityCheckboxChange(checked);
			this.proximityFormControl.reset(false);
			this.guarantee.proximityBuildingAnimals = false;
		}
	}

	onProximityCheckboxChange(checked: boolean) {
		this.guarantee.proximityBuildingAnimals = checked;
		if (!checked) {
			this.distanceFromSiteFormControl.reset('');
			this.guarantee.animalDistance = '';
		}
	}

	distanceFromSiteChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.animalDistance = target.value;
	}

	hazardousIndustryDistanceChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.hazardousIndustryDistance = target.value;
	}

	onUrbanAgglomerationCheckboxChange(checked: boolean) {
		this.guarantee.urbanOrIndustrialArea = checked;
		if (!checked) {
			this.onMainlyRefinerieCheckboxChange(checked);
			this.mainlyRefinerieFormControl.reset(false);
			this.guarantee.mainlyComprisingRefinerie = false;
		}
	}

	onMainlyRefinerieCheckboxChange(checked: boolean) {
		this.guarantee.mainlyComprisingRefinerie = checked;
		if (!checked) {
			this.hazardousIndustryDistanceFormControl.reset('');
			this.industryTypeFormControl.reset('');
			this.emissionsTypeFormControl.reset('');
			this.gasCarriedWindsFormControl.reset('');
			this.guarantee.hazardousIndustryDistance = '';
			this.guarantee.typeOfIndustry = '';
			this.guarantee.typeOfEmissions = '';
			this.guarantee.gasCarriedWinds = '';
		}
	}

	industryTypeChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.typeOfIndustry = target.value;
	}

	emissionsTypeChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.typeOfEmissions = target.value;
	}

	onGasCarriedWindsChange(event: MatRadioChange) {
		this.guarantee.gasCarriedWinds = String(event.value);
	}

	onDesertCheckboxChange(checked: boolean) {
		this.guarantee.inDesert = checked;
		if (!checked) {
			this.areaNameFormControl.reset('');
			this.guarantee.desertComment = '';
		}
	}

	areaNameChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.desertComment = target.value;
	}

	onCoastalOrMarineCheckboxChange(checked: boolean) {
		this.guarantee.coastalMarineEnvironment = checked;
		if (!checked) {
			this.onCoastCheckboxChange(checked);
			this.coastFormControl.reset(false);
			this.betweenTwoFormControl.reset(false);
			this.betweenTenFormControl.reset(false);
			this.distanceToSeaFormControl.reset('');
			this.distanceToSeaCheckFormControl.reset(false);
			this.specifyDistanceToSeaFormControl.reset('');
			this.guarantee.lessThan300mfromCoast = false;
			this.guarantee.between300m2kmfromCoast = false;
			this.guarantee.between2km10kmfromCoast = false;
			this.guarantee.distanceToSea = '';
			this.guarantee.buildingEnvBLSpecifyDistance = false;
			this.guarantee.buildingEnvBLPleaseSpecify = '';
		}
	}

	onCoastCheckboxChange(checked: boolean) {
		this.guarantee.lessThan300mfromCoast = checked;
		if (!checked) {
			this.directWettingFormControl.reset(false);
			this.partialOrMomentaryFormControl.reset(false);
			this.guarantee.withoutWettingSeaWater = false;
			this.guarantee.partialWettingSeaWater = false;
		}
	}

	onDirectWettingCheckboxChange(checked: boolean) {
		this.guarantee.withoutWettingSeaWater = checked;
	}

	onPartialOrMomentaryCheckboxChange(checked: boolean) {
		this.guarantee.partialWettingSeaWater = checked;
	}

	onBetweenTwoCheckboxChange(checked: boolean) {
		this.guarantee.between300m2kmfromCoast = checked;
	}

	onBetweenTenCheckboxChange(checked: boolean) {
		this.guarantee.between2km10kmfromCoast = checked;
	}

	onSpecificEnvironmentCheckboxChange(checked: boolean) {
		this.guarantee.specificEnvironment = checked;
		if (!checked) {
			this.distanceToLakeFormControl.reset('');
			this.guarantee.distancetoLakeRiver = '';
		}
	}

	distanceToLakeChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.distancetoLakeRiver = target.value;
	}

	distanceToSeaChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.distanceToSea = target.value;
	}

	buildingEnvAltitudeCheckboxChange(checked: boolean) {
		this.guarantee.buildingEnvAltitude = checked;
	}

	onRuralArea20KMCheckboxChange(checked: boolean) {
		this.guarantee.buildingEnvBLMoreThan20KMFromCoast = checked;
		if (!checked) {
			this.onProximityCheckboxChange(checked);
			this.proximityFormControl.reset(false);
			this.guarantee.proximityBuildingAnimals = false;
		}
	}

	distanceToSeaCheckChanged(checked: boolean) {
		this.guarantee.buildingEnvBLSpecifyDistance = checked;
		if (!checked) {
			this.specifyDistanceToSeaFormControl.reset('');
			this.guarantee.buildingEnvBLPleaseSpecify = '';
		}
	}

	specifyDistanceToSeaChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.buildingEnvBLPleaseSpecify = target.value;
	}

	getLongType() {
		switch (this.guarantee.applicationType) {
		case 'Magnelis-Solar':
			return ` - ${ this.translationService.getTR('guarantee.magnelissolar.long') }`;
		case 'Magnelis-Building':
			return ` - ${ this.translationService.getTR('guarantee.magnelisbuilding.long') }`;
		case 'Magnelis-Other application':
			return ` - ${ this.translationService.getTR('guarantee.magnelisothers.long') }`;
		case 'Organic Coating':
			return ` - ${ this.translationService.getTR('guarantee.organiccoating.long') }`;
		}
		return '';
	}
}
